/**
 * A country's extra questions on a hazard or incident report (ADR-0029): the
 * data fields of the published HAZ / INC template for the site, with the
 * template's show/hide rules and validation (`@sss/form-engine`). Event
 * templates support simple field types; others are not shown on the report.
 */
import type { ReactNode } from 'react';
import { EVENT_FIELD_TYPES } from '@sss/events';
import { isItemVisible, localize, type FieldItem, type SurveyContext, type Template, type ValidationIssue } from '@sss/form-engine';
import { Chips, Field, Input, Segmented, Select, Textarea } from '../../components';
import { useI18n } from '../../lib/i18n';

export function visibleEventFields(template: Template, answers: Record<string, unknown>, context: SurveyContext): FieldItem[] {
  const scope = { answers: { fields: answers, controls: {} }, context };
  const out: FieldItem[] = [];
  for (const s of template.sections)
    for (const it of s.items) if (it.kind === 'field' && EVENT_FIELD_TYPES.has(it.type) && isItemVisible(s, it, scope)) out.push(it);
  return out;
}

export function EventQuestions({
  template,
  answers,
  onChange,
  context,
  issues,
}: {
  template: Template;
  answers: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  context: SurveyContext;
  issues: ValidationIssue[];
}) {
  const { t, lang } = useI18n();
  const fields = visibleEventFields(template, answers, context);
  if (fields.length === 0) return null;
  const set = (key: string, v: unknown) => onChange({ ...answers, [key]: v === '' ? undefined : v });
  const l = (x: Parameters<typeof localize>[0]) => localize(x, lang, template.sourceLanguage);
  return (
    <>
      {fields.map((f) => {
        const issue = issues.find((i) => i.ref === f.key);
        const error = issue ? t(issue.code === 'REQUIRED' ? 'ui.events.form.required' : 'ui.events.form.checkAnswer') : undefined;
        const v = answers[f.key];
        let control: (id: string) => ReactNode;
        switch (f.type) {
          case 'textarea':
            control = (id) => <Textarea id={id} rows={3} value={(v as string) ?? ''} onChange={(e) => set(f.key, e.target.value)} />;
            break;
          case 'number':
          case 'rating':
            control = (id) => (
              <Input id={id} type="number" inputMode="decimal" min={f.min} max={f.max} value={v === undefined ? '' : String(v)} onChange={(e) => set(f.key, e.target.value === '' ? '' : Number(e.target.value))} />
            );
            break;
          case 'single_choice':
            control = (id) => (
              <Select id={id} value={(v as string) ?? ''} onChange={(e) => set(f.key, e.target.value)}>
                <option value="">{t('ui.events.form.choose')}</option>
                {(f.options ?? []).map((o) => (
                  <option key={o.code} value={o.code}>
                    {l(o.label)}
                  </option>
                ))}
              </Select>
            );
            break;
          case 'multi_choice': {
            const sel = Array.isArray(v) ? (v as string[]) : [];
            control = () => (
              <Chips<string>
                options={(f.options ?? []).map((o) => ({ value: o.code, label: l(o.label) }))}
                selected={sel}
                onToggle={(c) => set(f.key, sel.includes(c) ? sel.filter((x) => x !== c) : [...sel, c])}
              />
            );
            break;
          }
          case 'yes_no':
            control = () => (
              <Segmented<'YES' | 'NO'>
                label={l(f.label)}
                size="md"
                value={v === true ? 'YES' : v === false ? 'NO' : null}
                onChange={(x) => set(f.key, x === 'YES' ? true : x === 'NO' ? false : '')}
                options={[
                  { value: 'NO', label: t('ui.events.form.no') },
                  { value: 'YES', label: t('ui.events.form.yes') },
                ]}
              />
            );
            break;
          default: {
            const type = f.type === 'datetime' ? 'datetime-local' : f.type === 'phone' ? 'tel' : f.type === 'text' ? 'text' : f.type;
            control = (id) => <Input id={id} type={type} value={(v as string) ?? ''} onChange={(e) => set(f.key, e.target.value)} />;
          }
        }
        return (
          <Field key={f.id} label={l(f.label)} help={f.help ? l(f.help) : undefined} required={f.required} error={error}>
            {control}
          </Field>
        );
      })}
    </>
  );
}
